import React, { FunctionComponent, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IMAGES } from '../../utils/constants';
import { ACTIVE_COLOR, INACTIVE_COLOR } from '../../components/ui/formInput';
import { useUserForgotPassword } from '../../hooks/api/auth';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  email: ValidateField;
};

const ForgotPassword: FunctionComponent = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formValidation: ValidateFieldsState = {
    email: { invalid: false, error: '' },
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const { mutateAsync: forgotPassword } = useUserForgotPassword();

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!email.trim()) {
      errors.email = {
        invalid: true,
        error: 'Email is required',
      };
      valid = false;
    } else if (!isValidEmail(email)) {
      errors.email = {
        invalid: true,
        error: 'Please enter a valid email address',
      };
      valid = false;
    } else {
      errors.email = { invalid: false, error: '' };
    }
    setValidateFields(errors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }
    setError('');
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    // Save email value and disable button to prevent duplicate clicks
    const emailToSend = email.toLowerCase().trim();
    setIsSubmitting(true);
    try {
      const result = await forgotPassword({ 
        email: emailToSend,
      });
      if (result?.status === 200) {
        // Clear email field only on success
        setEmail('');
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 60000);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    } catch (err: any) {
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#c3183c',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          width: 1000,
          height: 520,
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: '50%',
            backgroundImage: `linear-gradient(rgba(255, 0, 64, 0.4), rgba(255, 0, 64, 0.4)), url(${IMAGES.bannerImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <Box
          sx={{
            width: '50%',
            bgcolor: '#f9f9f9',
            px: 6,
            py: 5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4 }}
          >
            <Grid>
              <img
                src={IMAGES.barksdaleLogo}
                alt="Barksdale Logo"
                style={{ height: 50 }}
              />
            </Grid>
            <Grid>
              <img
                src={IMAGES.extranetLogo}
                alt="Extranet Logo"
                style={{ height: 50 }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" color="#c3183c" fontWeight="bold" gutterBottom>
            Important
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, fontSize: '12px' }}>
            Due to stricter requirements we are not able to email your password.
            Instead we will send a temporary password which can be used to create a new password.
            <br /><br />
            Enter your email address to receive a temporary password.
            This is the email address on record that you use to log in to the extranet.
            The temporary password cannot be sent to any other address.
          </Typography>

          <TextField
            fullWidth
            label="Enter Email"
            variant="outlined"
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            id="outlined-error-helper-text"
            error={validateFields.email.invalid}
            helperText={validateFields.email.error}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            InputLabelProps={{
              sx: {
                color: email ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {
            success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Reset password link sent successfully! Please check your email for the link.
              </Alert>
            )
          }

          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              sx={{
                backgroundColor: '#c3183c',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#a31534',
                },
                '&:disabled': {
                  backgroundColor: '#cccccc',
                  color: '#666666',
                },
              }}
              onClick={handleSubmit}
            >
              {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
            </Button>
            <Button
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: '#c3183c',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#a31534',
                },
              }}
              onClick={() => navigate('/login')}
            >
              CANCEL
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
